import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useDebugStore } from '../../stores/debugStore';
import { JwtDebugSection } from './JwtDebugSection';
import { UserDataSection } from './UserDataSection';
import { TimeDebugSection } from './TimeDebugSection';
import { JobsDebugSection } from './JobsDebugSection';
import { PotsDebugSection } from './PotsDebugSection';
import { FeatureSwitchGate, Features } from '@billda/features-ui';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`debug-tabpanel-${index}`}
      aria-labelledby={`debug-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `debug-tab-${index}`,
    'aria-controls': `debug-tabpanel-${index}`,
  };
}

export const DebugPanel: React.FC = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { isDebugOpen, closeDebug, toggleDebug } = useDebugStore();
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleClose = () => {
    closeDebug();
  };

  // Keyboard shortcut: Ctrl+Shift+D (or Cmd+Shift+D on Mac)
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl (Windows/Linux) or Cmd (Mac) + Shift + D
      const isCtrlOrCmd = event.ctrlKey || event.metaKey;

      if (
        isCtrlOrCmd &&
        event.shiftKey &&
        (event.key === 'd' || event.key === 'D')
      ) {
        event.preventDefault();
        toggleDebug();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleDebug]);

  return (
    <Dialog
      open={isDebugOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: 'background.default',
          backgroundImage: 'none',
          minHeight: fullScreen ? '100vh' : '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="div">
            🐛 Debug Panel
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ opacity: 0.8 }}
          >
            Development Tools
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="debug panel tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="JWT Token" {...a11yProps(0)} />
          <Tab label="User Data" {...a11yProps(1)} />
          <Tab label="Time" {...a11yProps(2)} />
          <Tab label="Jobs" {...a11yProps(3)} />
          <Tab label="Pots" {...a11yProps(4)} />
        </Tabs>
      </Box>

      <DialogContent
        sx={{ p: 0, height: fullScreen ? 'calc(100vh - 120px)' : 'auto' }}
      >
        <Box sx={{ height: '100%', overflow: 'auto' }}>
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ p: 3 }}>
              <JwtDebugSection />
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: 3 }}>
              <UserDataSection />
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ p: 3 }}>
              <FeatureSwitchGate
                feature={Features.Debug.IsTimeManipulationEnabled}
                showDisabledMessage
              >
                <TimeDebugSection />
              </FeatureSwitchGate>
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <Box sx={{ p: 3 }}>
              <JobsDebugSection />
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <Box sx={{ p: 3 }}>
              <FeatureSwitchGate
                feature={Features.Debug.IsTimeManipulationEnabled}
                showDisabledMessage
              >
                <PotsDebugSection />
              </FeatureSwitchGate>
            </Box>
          </TabPanel>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
